/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { IFieldValidationError } from "../FieldTypes";
import { FC_ANCHOR, Icon, Tooltip } from "../../core";

import "./FieldStatus.scss";

export interface IFieldStatusProps {
	className?: string;
	errors?: IFieldValidationError[];
}

const FieldStatus: React.FC<IFieldStatusProps> = ({ className, errors }) => {
	const baseClass = BEM("edt-field-status");

	const iconElRef = useRef<HTMLSpanElement>();

	if (className) {
		baseClass.add(className);
	}

	if (Array.isArray(errors) && errors.length > 0) {
		return (
			<>
				<span className={baseClass.toClassName()} ref={iconElRef}>
					<Icon name="mdi/alert" />
				</span>
				<Tooltip
					for={iconElRef}
					anchor={FC_ANCHOR.CENTER_TOP}
					openDelay={1}
					className={baseClass.element("validation-errors-tooltip").toClassName()}
				>
					<ul className={baseClass.element("validation-errors").toClassName()}>
						{errors.map((item, index) => {
							return (
								<li key={index}>
									<span
										className={baseClass
											.element("validation-errors-message")
											.toClassName()}
									>
										{item.message}
									</span>
									{item.details && item.details.length > 0 ? (
										<ul
											className={baseClass
												.element("validation-errors-details")
												.toClassName()}
										>
											{item.details.map((detailItem) => (
												<li key={detailItem}>{detailItem}</li>
											))}
										</ul>
									) : null}
								</li>
							);
						})}
					</ul>
				</Tooltip>
			</>
		);
	} else {
		return null;
	}
};

const FieldStatusMemo = React.memo(FieldStatus);

export { FieldStatusMemo as FieldStatus };
